import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { Show } from '../types';
import { useAuth } from '../contexts/AuthContext';

// Query keys for hierarchical caching
export const showsQueryKeys = {
  all: ['shows'] as const,
  userShows: (userId: string) => [...showsQueryKeys.all, 'user', userId] as const,
};

// Data fetching functions
// Exported so AuthContext can prefetch shows the moment the auth user is
// known, instead of waiting for the dashboard to mount behind the profile
// snapshot.
export const fetchUserShows = async (): Promise<Show[]> => {
  const getUserShows = httpsCallable(functions, 'getUserShows');
  const result = await getUserShows({ platform: 'web' });
  
  const showsData = (result.data as any).shows.map((show: any) => ({
    ...show,
    id: show.id,
    title: show.title,
    venue: show.venue,
    description: show.description,
    headerImageUrl: show.headerImageUrl,
    headerThumbnailUrl: show.headerThumbnailUrl,
    ticketLink: show.ticketLink,
    genre: show.genre,
    startDate: show.startDate || null,
    endDate: show.endDate || null,
    createdAt: show.createdAt ? new Date(show.createdAt) : null,
    updatedAt: show.updatedAt ? new Date(show.updatedAt) : null,
    createdBy: show.createdBy,
    organizationId: show.organizationId,
    isActive: show.isActive,
  })) as Show[];
  
  return showsData;
};

const createShowApi = async (showData: {
  title: string;
  venue: string;
  genre: string;
  /**
   * Which resource folder template the new show gets. Omitting it makes the
   * backend fall back to musical_theatre, which is what every show got while
   * this field was missing from the type and silently dropped by the callers.
   */
  showType?: string;
  startDate?: string;
  endDate?: string;
  headerImageUrl?: string;
  /** Super admins only: the organisation they are acting in. */
  organizationId?: string;
}) => {
  const createShow = httpsCallable(functions, 'createShow');
  return await createShow(showData);
};

const updateShowApi = async (showData: {
  showId: string;
  title: string;
  venue: string;
  genre: string;
  startDate?: string;
  endDate?: string;
  headerImageUrl?: string;
  posterImageUrl?: string;
  performanceStartDate?: string;
  performanceEndDate?: string;
  performanceVenue?: string;
}) => {
  const updateShow = httpsCallable(functions, 'updateShow');
  return await updateShow(showData);
};

const deleteShowApi = async (showId: string) => {
  const deleteShow = httpsCallable(functions, 'deleteShow');
  return await deleteShow({ showId });
};

// Query hook
export const useShowsData = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: showsQueryKeys.userShows(user?.uid || ''),
    queryFn: fetchUserShows,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
    // Shows are edited from the mobile app too, which can't invalidate this
    // cache - so always revalidate on mount and focus. The localStorage
    // hydration still paints instantly; the refetch corrects it in the
    // background. Without this, a reload within staleTime served the
    // previous state (cover/type lagging one edit behind) indefinitely.
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    // Gated on the auth user only, deliberately not on the profile. getUserShows
    // loads the caller's profile server-side and authorises from that, so
    // waiting for the client's profile snapshot here bought nothing and forced
    // three round trips to run end to end (auth -> profile listen -> shows).
    // Dropping the profile gate lets the shows fetch overlap the profile
    // snapshot instead of queuing behind it.
    enabled: !!user?.uid,
  });
};

// Mutation hooks
export const useShowsMutations = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  const invalidateShowsData = async () => {
    if (!user?.uid) return;
    await queryClient.invalidateQueries({ queryKey: showsQueryKeys.userShows(user.uid) });
  };

  const createShow = useMutation({
    mutationFn: createShowApi,
    onSuccess: async () => {
      await invalidateShowsData();
    },
    onError: (error) => {
      console.error('❌ React Query: Error creating show:', error);
    },
  });

  const updateShow = useMutation({
    mutationFn: updateShowApi,
    onSuccess: async () => {
      await invalidateShowsData();
    },
    onError: (error) => {
      console.error('❌ React Query: Error updating show:', error);
    },
  });

  const deleteShow = useMutation({
    mutationFn: deleteShowApi,
    onSuccess: async () => {
      await invalidateShowsData();
    },
    onError: (error) => {
      console.error('❌ React Query: Error deleting show:', error);
    },
  });

  return {
    createShow,
    updateShow,
    deleteShow,
    invalidateShowsData,
  };
};